import React from 'react';

const ShippingPage = () => {
  return (
    <div className="shipping-page">
      <div className="container">
        <div className="content-wrapper">
          <h1>Shipping & Delivery Policy</h1>
          
          <div className="content">
            <p><strong>Effective Date:</strong> January 2025</p>
            
            <p>This shipping and delivery policy outlines how your orders are delivered from Bunny Boba. Please note that we specialize in <strong>fresh beverage delivery</strong> through established food delivery platforms and do not provide traditional shipping services.</p>

            <h2>Our Delivery Model</h2>
            
            <p>Bunny Boba operates a <strong>delivery-focused business model</strong> where we prepare fresh beverages and deliver them through trusted third-party delivery partners. We do not operate our own shipping or postal services.</p>

            <h3>No Traditional Shipping</h3>
            <p>Important: <strong>We do NOT provide traditional shipping services</strong> such as:</p>
            <ul>
              <li>Postal mail delivery</li>
              <li>Courier services</li>
              <li>Package shipping</li>
              <li>Nationwide courier delivery</li>
              <li>International shipping</li>
              <li>Bulk or wholesale shipping</li>
            </ul>

            <h2>Delivery Partners</h2>

            <h3>Primary Delivery Platforms</h3>
            <p>We deliver your fresh boba tea orders exclusively through the following established food delivery platforms:</p>
            
            <div className="delivery-partners">
              <div className="partner-card">
                <h4>🍔 Zomato</h4>
                <p>Order through the Zomato app or website</p>
                <ul>
                  <li>Real-time order tracking</li>
                  <li>Multiple payment options</li>
                  <li>Customer support through Zomato</li>
                  <li>Delivery time: 30-45 minutes typically</li>
                </ul>
              </div>

              <div className="partner-card">
                <h4>🛵 Swiggy</h4>
                <p>Order through the Swiggy app or website</p>
                <ul>
                  <li>Live order tracking</li>
                  <li>Various payment methods</li>
                  <li>Swiggy customer service</li>
                  <li>Delivery time: 30-45 minutes typically</li>
                </ul>
              </div>
            </div>

            <h3>Website Orders</h3>
            <p>Orders placed directly through our website (www.bunny-boba.com) are:</p>
            <ul>
              <li><strong>Prepared fresh</strong> at our location</li>
              <li><strong>Delivered via our delivery partners</strong> (Zomato/Swiggy)</li>
              <li><strong>Tracked through our platform</strong> and the delivery partner's system</li>
              <li><strong>Subject to delivery partner policies</strong> for delivery terms</li>
            </ul>

            <h2>Delivery Areas</h2>

            <h3>Service Coverage</h3>
            <p>Our delivery coverage is determined by our delivery partners' service areas:</p>
            <ul>
              <li><strong>Primary area:</strong> Greater Noida and surrounding regions</li>
              <li><strong>Coverage depends on:</strong> Zomato and Swiggy service availability</li>
              <li><strong>Distance limitations:</strong> Set by delivery partner algorithms</li>
              <li><strong>Real-time availability:</strong> Check delivery partner apps for current coverage</li>
            </ul>

            <h3>Delivery Availability</h3>
            <p>Delivery is available when:</p>
            <ul>
              <li>Our store is open and operational</li>
              <li>Delivery partners are active in your area</li>
              <li>Weather conditions permit safe delivery</li>
              <li>Your address is within the delivery radius</li>
            </ul>

            <h2>Delivery Process</h2>

            <h3>Order Preparation</h3>
            <ol>
              <li><strong>Order Received:</strong> We receive your order through our platform or delivery partner</li>
              <li><strong>Preparation Start:</strong> Fresh preparation begins immediately</li>
              <li><strong>Quality Check:</strong> Each drink is checked for quality and accuracy</li>
              <li><strong>Packaging:</strong> Secure packaging to prevent spills during transport</li>
              <li><strong>Handover:</strong> Order is given to delivery partner</li>
            </ol>

            <h3>Delivery Execution</h3>
            <p>Once your order is ready:</p>
            <ul>
              <li><strong>Pickup by delivery partner</strong> from our location</li>
              <li><strong>Real-time tracking</strong> available through delivery apps</li>
              <li><strong>Direct delivery</strong> to your specified address</li>
              <li><strong>Contactless delivery</strong> options available</li>
            </ul>

            <h2>Delivery Times</h2>

            <h3>Standard Delivery</h3>
            <p>Typical delivery timeframes:</p>
            <ul>
              <li><strong>Preparation time:</strong> 10-20 minutes (depending on order complexity)</li>
              <li><strong>Delivery time:</strong> 20-35 minutes (depending on distance and traffic)</li>
              <li><strong>Total time:</strong> 30-55 minutes from order confirmation</li>
            </ul>

            <h3>Factors Affecting Delivery Time</h3>
            <ul>
              <li><strong>Order complexity:</strong> Multiple drinks or customizations may take longer</li>
              <li><strong>Distance:</strong> Farther addresses require more travel time</li>
              <li><strong>Traffic conditions:</strong> Peak hours may cause delays</li>
              <li><strong>Weather:</strong> Rain or storms may slow delivery</li>
              <li><strong>Delivery partner availability:</strong> Busy periods may extend wait times</li>
              <li><strong>Store hours:</strong> Orders near closing time may be prioritized differently</li>
            </ul>

            <h2>Delivery Charges</h2>

            <h3>Fee Structure</h3>
            <p>Delivery charges are determined by:</p>
            <ul>
              <li><strong>Delivery partner fees:</strong> Set by Zomato/Swiggy based on distance</li>
              <li><strong>Platform charges:</strong> Service fees from delivery apps</li>
              <li><strong>Surge pricing:</strong> During peak hours or high demand periods</li>
              <li><strong>Distance-based:</strong> Longer distances typically cost more</li>
            </ul>

            <h3>Free Delivery</h3>
            <p>Free delivery may be available:</p>
            <ul>
              <li>During promotional periods</li>
              <li>For orders above minimum value (as set by delivery partners)</li>
              <li>For premium membership holders of delivery platforms</li>
              <li>During special events or campaigns</li>
            </ul>

            <h2>Packaging and Product Protection</h2>

            <h3>Specialized Beverage Packaging</h3>
            <p>We use specialized packaging designed for beverage delivery:</p>
            <ul>
              <li><strong>Spill-proof containers:</strong> Secure lids and proper sealing</li>
              <li><strong>Temperature retention:</strong> Insulated bags for hot/cold drinks</li>
              <li><strong>Stable transport:</strong> Carrier systems to prevent tipping</li>
              <li><strong>Protection padding:</strong> Extra protection for delicate items</li>
            </ul>

            <h3>Delivery Safety</h3>
            <p>We ensure safe delivery through:</p>
            <ul>
              <li><strong>Secure packaging:</strong> Tamper-evident seals when possible</li>
              <li><strong>Proper handling instructions:</strong> Clear instructions to delivery partners</li>
              <li><strong>Quality control:</strong> Final check before handover</li>
              <li><strong>Hygiene standards:</strong> Clean packaging and handling</li>
            </ul>

            <h2>Delivery Issues and Resolution</h2>

            <h3>Common Delivery Issues</h3>
            <ul>
              <li><strong>Delayed delivery:</strong> Contact delivery partner for updates</li>
              <li><strong>Wrong address:</strong> Verify address details immediately</li>
              <li><strong>Spilled/damaged items:</strong> Report to us and delivery partner</li>
              <li><strong>Missing items:</strong> Check with delivery person and contact support</li>
              <li><strong>Temperature issues:</strong> Report if drinks are not at proper temperature</li>
            </ul>

            <h3>Resolution Process</h3>
            <ol>
              <li><strong>Immediate contact:</strong> Contact delivery partner's customer service</li>
              <li><strong>Document the issue:</strong> Take photos if applicable</li>
              <li><strong>Contact us:</strong> Reach out to Bunny Boba for product-related issues</li>
              <li><strong>Resolution:</strong> Refund, replacement, or credit as appropriate</li>
            </ol>

            <h2>Pickup Options</h2>

            <h3>Self-Pickup Available</h3>
            <p>You can also pick up your order directly from our location:</p>
            <ul>
              <li><strong>Address:</strong> FLOOR LG TOWN CENTRAL, LGB-08, BLOCK B, C04 SEC 16B, Greater Noida GHORI, Greater Noida, India</li>
              <li><strong>Advance ordering:</strong> Place order online or call ahead</li>
              <li><strong>Ready-time notification:</strong> We'll notify when your order is ready</li>
              <li><strong>No delivery charges:</strong> Save on delivery fees</li>
              <li><strong>Immediate availability:</strong> Get your order as soon as it's ready</li>
            </ul>

            <h2>Special Delivery Instructions</h2>

            <h3>Contactless Delivery</h3>
            <p>For contactless delivery:</p>
            <ul>
              <li>Select contactless option during checkout</li>
              <li>Provide specific drop-off instructions</li>
              <li>Maintain safe distance during delivery</li>
              <li>Payment through digital methods preferred</li>
            </ul>

            <h3>Delivery Instructions</h3>
            <p>Help ensure smooth delivery by providing:</p>
            <ul>
              <li><strong>Clear address:</strong> Include building name, floor, and unit number</li>
              <li><strong>Contact number:</strong> Reachable phone number</li>
              <li><strong>Landmarks:</strong> Nearby landmarks for easy location</li>
              <li><strong>Special instructions:</strong> Gate codes, security requirements, etc.</li>
            </ul>

            <h2>Business Hours and Delivery Windows</h2>

            <h3>Operating Hours</h3>
            <p>Delivery is available during our business hours:</p>
            <ul>
              <li><strong>All 7 days:</strong> 11:30 AM - 10:00 PM</li>
              <li><strong>Weekend hours:</strong> May vary - check delivery apps for current hours</li>
              <li><strong>Holiday schedules:</strong> Special hours during holidays</li>
            </ul>

            <h3>Last Order Times</h3>
            <ul>
              <li>Orders must be placed at least 30 minutes before closing</li>
              <li>Complex orders may require additional time</li>
              <li>Late orders may be declined to ensure quality</li>
            </ul>

            <h2>Contact Information</h2>
            
            <div className="contact-section">
              <h3>Delivery Support</h3>
              <p><strong>For order status:</strong> Check delivery partner app or contact their support</p>
              <p><strong>For product issues:</strong> Contact Bunny Boba directly</p>
              <p><strong>Business Hours:</strong> All 7 days (11:30 AM - 10:00 PM)</p>
              <p><strong>Address:</strong> FLOOR LG TOWN CENTRAL, LGB-08, BLOCK B, C04 SEC 16B, Greater Noida GHORI, Greater Noida, India</p>
              <p><strong>Response Time:</strong> Within 24 hours for product-related issues</p>
            </div>

            <h2>Important Notes</h2>
            
            <div className="important-note">
              <h3>Key Points to Remember</h3>
              <ul>
                <li>We do NOT provide traditional shipping or postal services</li>
                <li>All deliveries are handled through Zomato and Swiggy platforms</li>
                <li>Delivery terms are subject to delivery partner policies</li>
                <li>Fresh beverages are best consumed immediately upon delivery</li>
                <li>Contact appropriate parties (us for product, delivery partner for delivery) for issues</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .shipping-page {
          min-height: 100vh;
          background-color: #fff8f0;
          padding: 40px 0 80px;
        }

        .content-wrapper {
          max-width: 800px;
          margin: 0 auto;
          background: white;
          padding: 40px;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        h1 {
          color: #C2185B;
          text-align: center;
          margin-bottom: 30px;
          font-size: 2.5rem;
        }

        h2 {
          color: #C2185B;
          margin-top: 40px;
          margin-bottom: 20px;
          font-size: 1.8rem;
          border-bottom: 2px solid #C2185B;
          padding-bottom: 10px;
        }

        h3 {
          color: #C2185B;
          margin-top: 30px;
          margin-bottom: 15px;
          font-size: 1.3rem;
        }

        h4 {
          color: #C2185B;
          margin-bottom: 10px;
          font-size: 1.1rem;
        }

        .content p {
          margin-bottom: 15px;
          line-height: 1.6;
          color: #333;
          text-align: justify;
        }

        .content ul, .content ol {
          margin-bottom: 20px;
          padding-left: 25px;
        }

        .content li {
          margin-bottom: 8px;
          line-height: 1.6;
          color: #333;
        }

        .content strong {
          color: #C2185B;
          font-weight: 600;
        }

        .delivery-partners {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin: 30px 0;
        }

        .partner-card {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 15px;
          border-left: 4px solid #C2185B;
        }

        .partner-card h4 {
          margin-top: 0;
          font-size: 1.2rem;
        }

        .partner-card p {
          margin-bottom: 15px;
          font-weight: 500;
          text-align: left;
        }

        .partner-card ul {
          margin-bottom: 0;
        }

        .contact-section {
          background: #f8f9fa;
          padding: 25px;
          border-radius: 10px;
          border-left: 4px solid #C2185B;
          margin: 30px 0;
        }

        .contact-section h3 {
          margin-top: 0;
          color: #C2185B;
        }

        .contact-section p {
          margin-bottom: 10px;
          text-align: left;
        }

        .important-note {
          background: linear-gradient(135deg, #fff0f5, #fce4ec);
          padding: 25px;
          border-radius: 15px;
          border: 2px solid #C2185B;
          margin-top: 40px;
        }

        .important-note h3 {
          margin-top: 0;
          color: #C2185B;
          text-align: center;
        }

        .important-note ul {
          text-align: left;
        }

        .important-note li {
          font-weight: 500;
          color: #333;
        }

        @media (max-width: 768px) {
          .content-wrapper {
            margin: 0 20px;
            padding: 30px 20px;
          }

          h1 {
            font-size: 2rem;
          }

          h2 {
            font-size: 1.5rem;
          }

          h3 {
            font-size: 1.2rem;
          }

          .delivery-partners {
            grid-template-columns: 1fr;
          }

          .content p, .content li {
            text-align: left;
          }
        }
      `}</style>
    </div>
  );
};

export default ShippingPage; 